import { AASpinner } from "@bagel-web/components";
import { QueryClient, useQuery } from "@tanstack/react-query";
import { fetchFeatureFlags } from "api";
import featureFlagQueries from "queries/featureFlag";
import { createContext, useContext } from "react";
import { Alert } from "react-bootstrap";

// Maintain a static list of feature flag names to be resolved for the dashboard.
export const FeatureFlags = Object.freeze({
  DASHBOARD_PROJECT_USER_MANAGEMENT: "dashboard.project_user_management",
  DASHBOARD_ORG_SCOPED_RBAC: "dashboard.org_scope_rbac",
  // BAGEL-312 + BAGEL-448: gates both auth-model credential tabs
  // (M2M credentials and OIDC issuers) during initial rollout so we
  // can enable per-org for beta studios before flipping it on
  // globally.
  DASHBOARD_M2M_CREDENTIALS: "dashboard.m2m_credentials",
  // BAGEL-414 + BAGEL-533: the Playground's SSO replay mode mints a
  // runtime token from the developer's own AuthKit session, which only
  // works where the backend trusts the platform issuer and the org is
  // enrolled. Same flag the mint endpoint checks, so the option is not
  // offered where the replay would fail.
  AUTH_DEVELOPER_SSO: "auth.developer_sso_enabled",
});

const getFeatureFlagQuery = () => {
  const flagNames = Object.values(FeatureFlags);
  return {
    queryKey: featureFlagQueries.list(flagNames),
    queryFn: () => fetchFeatureFlags(flagNames),
    staleTime: Infinity,
  };
};

export const initializeFeatureFlags = async (queryClient: QueryClient) => {
  await queryClient.prefetchQuery(getFeatureFlagQuery());
};

export const isFeatureFlagEnabledAsync = async (
  queryClient: QueryClient,
  flagName: string
) => {
  const featureFlags = await queryClient.fetchQuery(getFeatureFlagQuery());
  return !!featureFlags[flagName];
};

export type FeatureFlagsContextType = {
  isFeatureFlagEnabled: (flagName: string) => boolean;
};

export const FeatureFlagsContext =
  createContext<FeatureFlagsContextType | null>(null);

export const FeatureFlagsProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const {
    data: featureFlagValues,
    isPending,
    isError,
  } = useQuery(getFeatureFlagQuery());

  const isFeatureFlagEnabled = (flagName: string): boolean => {
    return !!featureFlagValues?.[flagName];
  };

  // TODO: After migrating to React 19, use the Suspense / use pattern to simplify.
  return (
    <FeatureFlagsContext.Provider
      value={featureFlagValues ? { isFeatureFlagEnabled } : null}
    >
      {isPending && <AASpinner />}
      {isError && <Alert variant="danger">Something went wrong.</Alert>}
      {featureFlagValues && children}
    </FeatureFlagsContext.Provider>
  );
};

export const useFeatureFlags = () => {
  const context = useContext(FeatureFlagsContext);
  if (!context) {
    throw new Error(
      "useFeatureFlags must be used within an initialized FeatureFlagsProvider"
    );
  }
  return context;
};

/**
 * Read a single feature flag value directly from the React Query cache.
 *
 * Unlike ``useFeatureFlags``, this works *outside* a
 * ``FeatureFlagsProvider`` — useful for shared layouts and other
 * components that need to soft-gate a single piece of UI without
 * adding a Provider boundary + spinner above them.
 *
 * Returns ``false`` while the query is loading or if it errors,
 * which is the right default for hiding gated UI: a tab that
 * briefly flickers in and then disappears is worse than one that
 * stays hidden until the flag is loaded.
 */
export const useFeatureFlagValue = (flagName: string): boolean => {
  const { data } = useQuery(getFeatureFlagQuery());
  return !!data?.[flagName];
};
